// main steps enum

import { OnboardingProgress } from '@/core/models/staff.model'
import { StepProps } from '@/pages/onboarding/components/StepComponent'
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUser'
import PatientIcon from '@/components/ui/icons/Patient'
import ConsultationIcon from '@/components/ui/icons/Consultation'
import ExpertiseIcon from '@/components/ui/icons/Expertise'
import LinkIcon from '@mui/icons-material/Link'
import ChairIcon from '@mui/icons-material/Chair'
import SettingsIcon from '@mui/icons-material/Settings'
import {
  AccountSetupSteps,
  ConsultFirstPatientSteps,
  DoctorExpertiseSteps,
  FirstTeleconsultationSteps,
  MainSteps,
  OnlineAppointmentsSteps,
  VideoConfEquipmentSteps,
  WaitingRoomSteps,
} from './enum'
import { BasicStepProps } from './type'
import { accountSetupStepsFunctionMapping, consultFirstPatientStepsFunctionMapping, doctorExpertiseStepsFunctionMapping, firstTeleconsultationStepsFunctionMapping, onlineAppointmentsStepsFunctionMapping, videoConfEquipmentStepsFunctionMapping, waitingRoomStepsFunctionMapping } from './mapping'

// user type onboarding steps

// component mapping
export const OnboardingMapping: Record<MainSteps, BasicStepProps> = {
  [MainSteps.COMPLETE_ACCOUNT_SETUP]: {
    icon: <VerifiedUserOutlinedIcon />,
    childSteps: Object.values(AccountSetupSteps).map((step) => ({
      label: step,
      checked: false,
      redirect: () => {
        return 'account-verification'
      },
      showUser: () => { },
    })),
    totalNumberOfChildSteps: Object.values(AccountSetupSteps).length,
    numberOfCompletedChildSteps: 0, // you can update this dynamically based on user progress
  },
  [MainSteps.CONSULT_FIRST_PATIENT]: {
    icon: <PatientIcon color="inherit" />,
    childSteps: Object.values(ConsultFirstPatientSteps).map((step) => ({
      label: step,
      checked: false,
      redirect: () => {
        return 'patients/create'
      },
      showUser: () => { },
    })),
    totalNumberOfChildSteps: Object.values(ConsultFirstPatientSteps).length,
    numberOfCompletedChildSteps: 0,
  },
  [MainSteps.PERFORM_FIRST_TELECONSULTATION]: {
    icon: <ConsultationIcon htmlColor="white" />,
    childSteps: Object.values(FirstTeleconsultationSteps).map((step) => ({
      label: step,
      checked: false,
      redirect: () => {
        return 'consultations/create'
      },
      showUser: () => { },
    })),
    totalNumberOfChildSteps: Object.values(FirstTeleconsultationSteps).length,
    numberOfCompletedChildSteps: 0,
  },
  [MainSteps.REQUEST_DOCTOR_EXPERTISE]: {
    icon: <ExpertiseIcon />,
    childSteps: Object.values(DoctorExpertiseSteps).map((step) => ({
      label: step,
      checked: false,
      redirect: () => {
        return 'expertises/create'
      },
      showUser: () => { },
    })),
    totalNumberOfChildSteps: Object.values(DoctorExpertiseSteps).length,
    numberOfCompletedChildSteps: 0,
  },
  [MainSteps.ACTIVATE_ONLINE_APPOINTMENTS]: {
    icon: <LinkIcon />,
    childSteps: Object.values(OnlineAppointmentsSteps).map((step) => ({
      label: step,
      checked: false,
      redirect: () => {
        return 'meeting-links/create'
      },
      showUser: () => { },
    })),
    totalNumberOfChildSteps: Object.values(OnlineAppointmentsSteps).length,
    numberOfCompletedChildSteps: 0,
  },
  [MainSteps.CREATE_WAITING_ROOM]: {
    icon: <ChairIcon />,
    childSteps: Object.values(WaitingRoomSteps).map((step) => ({
      label: step,
      checked: false,
      redirect: () => {
        return 'waiting-rooms/create'
      },
      showUser: () => { },
    })),
    totalNumberOfChildSteps: Object.values(WaitingRoomSteps).length,
    numberOfCompletedChildSteps: 0,
  },
  [MainSteps.CONFIGURE_VIDEO_CONFERENCE_EQUIPMENT]: {
    icon: <SettingsIcon />,
    childSteps: Object.values(VideoConfEquipmentSteps).map((step) => ({
      label: step,
      checked: false,
      redirect: () => {
        return 'configuration?section=deviceForm'
      },
      showUser: () => { },
    })),
    totalNumberOfChildSteps: Object.values(VideoConfEquipmentSteps).length,
    numberOfCompletedChildSteps: 0,
  },
}

export const mapOnboardingProgressToStepsProps = (progress: OnboardingProgress): StepProps[] => {
  return Object.keys(progress)
    .map((mainStepKey) => {
      const mainStep = OnboardingMapping[mainStepKey as MainSteps] // Cast key as MainSteps

      if (!mainStep) {
        console.error(`Main step ${mainStepKey} not found in OnboardingMapping.`)
        return null
      }

      switch (mainStepKey) {
        case MainSteps.COMPLETE_ACCOUNT_SETUP:
          mainStep.childSteps.forEach((childStep) => {
            const funcs = accountSetupStepsFunctionMapping[childStep.label as AccountSetupSteps]
            if (funcs) {
              childStep.redirect = funcs.redirect
              childStep.showUser = funcs.showUser
            }
          })
          break
        case MainSteps.CONSULT_FIRST_PATIENT:
          mainStep.childSteps.forEach((childStep) => {
            const funcs = consultFirstPatientStepsFunctionMapping[childStep.label as ConsultFirstPatientSteps]
            if (funcs) {
              childStep.redirect = funcs.redirect
              childStep.showUser = funcs.showUser
            }
          })
          break
        case MainSteps.PERFORM_FIRST_TELECONSULTATION:
          mainStep.childSteps.forEach((childStep) => {
            const funcs = firstTeleconsultationStepsFunctionMapping[childStep.label as FirstTeleconsultationSteps]
            if (funcs) {
              childStep.redirect = funcs.redirect
              childStep.showUser = funcs.showUser
            }
          })
          break
        case MainSteps.REQUEST_DOCTOR_EXPERTISE:
          mainStep.childSteps.forEach((childStep) => {
            const funcs = doctorExpertiseStepsFunctionMapping[childStep.label as DoctorExpertiseSteps]
            if (funcs) {
              childStep.redirect = funcs.redirect
              childStep.showUser = funcs.showUser
            }
          })
          break
        case MainSteps.ACTIVATE_ONLINE_APPOINTMENTS:
          mainStep.childSteps.forEach((childStep) => {
            const funcs = onlineAppointmentsStepsFunctionMapping[childStep.label as OnlineAppointmentsSteps]
            if (funcs) {
              childStep.redirect = funcs.redirect
              childStep.showUser = funcs.showUser
            }
          })
          break
        case MainSteps.CREATE_WAITING_ROOM:
          mainStep.childSteps.forEach((childStep) => {
            const funcs = waitingRoomStepsFunctionMapping[childStep.label as WaitingRoomSteps]
            if (funcs) {
              childStep.redirect = funcs.redirect
              childStep.showUser = funcs.showUser
            }
          })
          break
        case MainSteps.CONFIGURE_VIDEO_CONFERENCE_EQUIPMENT:
          mainStep.childSteps.forEach((childStep) => {
            const funcs = videoConfEquipmentStepsFunctionMapping[childStep.label as VideoConfEquipmentSteps]
            if (funcs) {
              childStep.redirect = funcs.redirect
              childStep.showUser = funcs.showUser
            }
          })
          break
        default:
          break
      }

      const updatedChildSteps = mainStep.childSteps.map((childStep) => {
        const isChecked = progress[mainStepKey as MainSteps][childStep.label] || false
        return { ...childStep, checked: isChecked }
      })

      const numberOfCompletedChildSteps = updatedChildSteps.filter((step) => step.checked).length

      return {
        ...mainStep,
        title: mainStepKey,
        description: mainStepKey,
        childSteps: updatedChildSteps,
        numberOfCompletedChildSteps: numberOfCompletedChildSteps,
        totalNumberOfChildSteps: updatedChildSteps.length,
      }
    })
    .filter(Boolean) as StepProps[]
}
